import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import PageHeader from "../../../core/common/commonComponents/PageHeader";
import StatusFilterHeader from "../../../core/common/commonComponents/StatusFilterHeader";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { exportToCSV, exportToExcel } from "../../../core/common/GlobalFunctions/exportTableData";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import DeclarationFormModal from "./modal/declarationFormModal";
import {deleteTdsDeclaration,fetchTdsDeclarations,fetchTdsDeclarationsByEmployeeId} from "../../../core/data/redux/payroll/TDS/tdsDelcaration/tdsDeclarationAction";
import { getEmployeeByUserId } from "../../../core/data/redux/employee/employeeAction";
import { decryptData } from "../../../core/utils/encryptUtils";
import { formatCurrency } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";

const DeclarationForm = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((s: any) => s.auth);
  const { employeesbyUser } = useSelector((s: any) => s.employee);
  const employeeId = employeesbyUser?.employeeId;

  // Permission
  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");

  const config = useAppConfig();

  const { tdsDeclaration } = useSelector((s: any) => s.TDS || {});
  const employeeDeclarations: any[] = tdsDeclaration?.employeeDeclarations || [];

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedGroup, setSelectedGroup] = useState<any>(null);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{ EmployeeID: number; FinancialYearID: number } | null>(null);

  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setModalType("");
    setIsModalVisible(false);
    setSelectedGroup(null);
  };

  useEffect(() => {
    if (user?.UserId) {
      dispatch(getEmployeeByUserId({ userId: user?.UserId }));
    }
  }, [dispatch, user?.UserId]);

  useEffect(() => {
    if (employeeId) {
      dispatch(fetchTdsDeclarationsByEmployeeId(employeeId));
    }
  }, [dispatch, employeeId]);

  const grouped = useMemo(() => {
    const map = new Map<
      string,
      {
        EmployeeID: number;
        EmployeeName: string;
        FinancialYearID: number;
        FinancialYearName: string;
        sections: any[];
        totalDeclared: number;
        lastModified: string | null;
      }
    >();

    employeeDeclarations.forEach((d) => {
      const k = `${d.EmployeeID}|${d.FinancialYearID}`;
      if (!map.has(k)) {
        map.set(k, {
          EmployeeID: Number(d.EmployeeID),
          EmployeeName: d.EmployeeName || "N/A",
          FinancialYearID: Number(d.FinancialYearID),
          FinancialYearName: d.FinancialYearName || "N/A",
          sections: [],
          totalDeclared: 0,
          lastModified: null,
        });
      }
      const g = map.get(k)!;
      g.sections.push(d);
      g.totalDeclared += Number(d.DeclaredAmount || 0);
      const mod = d.ModifiedAt || d.CreatedAt;
      if (!g.lastModified || new Date(mod) > new Date(g.lastModified)) {
        g.lastModified = mod;
      }
    });

    return Array.from(map.values());
  }, [employeeDeclarations]);

  const filteredData = useMemo(() => {
    return grouped;
  }, [grouped]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, selectedStatus, selectedSort, employeeDeclarations]);

  const exportData = filteredData.map((g: any) => ({
    EmployeeName: g.EmployeeName,
    FinancialYearName: g.FinancialYearName,
    SectionsCount: g.sections.length,
    TotalDeclared: g.totalDeclared,
    LastModified:
      g.lastModified ? new Date(g.lastModified).toLocaleString() : "N/A",
  }));

  // Delete: remove ALL lines of that (EmployeeID, FY)
  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;
    const groupLines = employeeDeclarations.filter(
      (d) =>
        Number(d.EmployeeID) === Number(groupToDelete.EmployeeID) &&
        Number(d.FinancialYearID) === Number(groupToDelete.FinancialYearID)
    );

    let failed = false;
    for (const line of groupLines) {
      const res = await dispatch(deleteTdsDeclaration(Number(line.DeclarationID)));
      if (res.meta.requestStatus !== "fulfilled") failed = true;
    }
    if (employeeId) {
      dispatch(fetchTdsDeclarationsByEmployeeId(employeeId));
    }
    if (!failed) {
      message.success("Declaration removed for this Financial Year.");
      setIsDeleteModalVisible(false);
      await dispatch(fetchTdsDeclarations()); // refresh
    } else {
      message.error("Some lines failed to delete. Please retry.");
    }
  };

  const columns = [
    { title: "Employee", dataIndex: "EmployeeName" },
    { title: "Financial Year", dataIndex: "FinancialYearName" },
    { title: "Sections Counts", dataIndex: "sections", render: (arr: any[]) => arr.length },
    {
      title: "Total Declared",
      dataIndex: "totalDeclared",
      render: (val: number) => formatCurrency(val || 0, config)

    },
    {
      title: "Action",
      render: (_: any, record: any) => (
        <div className="action-icon d-inline-flex">
          {canEdit && (
            <button
              className="me-2"
              onClick={() => {
                setSelectedGroup({
                  EmployeeID: record.EmployeeID,
                  FinancialYearID: record.FinancialYearID,
                });
                showModal("edit");
              }}
              title="Edit"
            >
              <i className="ti ti-edit" />
            </button>
          )}

          {canDelete && (
            <button
              onClick={() => {
                setGroupToDelete({
                  EmployeeID: record.EmployeeID,
                  FinancialYearID: record.FinancialYearID,
                });
                setIsDeleteModalVisible(true);
              }}
              title="Delete"
            >
              <i className="ti ti-trash" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <PageHeader
            title="Declaration Form"
            breadcrumbs={[
              { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
              { label: "TDS" },
              { label: "Declaration Form", isActive: true },
            ]}
            listViewPath="#"
            onExportCSV={canExport ? () => exportToCSV(exportData, "TdsDeclarations_Groups") : undefined}
            onExportExcel={canExport ? () => exportToExcel(exportData, "TdsDeclarations_Groups") : undefined}
            onAdd={canAdd ? () => showModal("create") : undefined}
            addLabel="Add Declaration"
            exportEnabled={canExport}
            addButtonEnabled={canAdd}
            rightExtra={<CollapseHeader />}
          />

          <div className="card">
            <StatusFilterHeader
              title="Declaration List"
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedSort={selectedSort}
              setSelectedSort={setSelectedSort}
            />
            <div className="card-body p-0">
              <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
            </div>
          </div>
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>

      <DeclarationFormModal
        modalType={modalType}
        handleCancel={handleCancel}
        isModalVisible={isModalVisible}
        selectedRowData={selectedGroup} // ⬅ pass only {EmployeeID, FinancialYearID} for EDIT
        setTableKey={setTableKey}
      />

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDeleteGroup}
      />
    </>
  );
};

export default DeclarationForm;
